import { useAuth } from '@clerk/react'
import { useLocation } from '#router'
import {
  type ClerkReachableSettingsSlug,
  settingsSectionHref,
  settingsSectionLabel,
} from '@/components/settings/sections'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { openUserProfileTab } from '@/lib/clerk-navigation'

export interface OpenSettingsSectionOptions {
  /** Open in a new browser tab, keeping the current page (e.g. the editor) alive. */
  newTab?: boolean
  /**
   * An element id inside the section; the router scrolls to it on arrival.
   * Dropped on the Clerk fallback, which can only open the tab.
   */
  hash?: string
}

/**
 * The one way to deep-link into a settings section. Signed in with
 * `enable-settings-page` on, it goes to the first-party `/settings/<slug>`
 * page; otherwise (flag off or still loading, or signed out) it opens the
 * matching tab of Clerk's profile modal, so users without the flag never hit
 * the guarded route's 404 and signed-out users are not sent to a sign-in wall
 * (for them the Clerk path is a no-op, as it always was).
 *
 * The Clerk branch is deleted at cutover (LG-1086) along with the modal.
 */
export function useOpenSettingsSection(): (
  slug: ClerkReachableSettingsSlug,
  options?: OpenSettingsSectionOptions,
) => void {
  const { isSignedIn } = useAuth()
  const { enabled } = useFeatureFlag('enable-settings-page')
  const { navigate } = useLocation()

  return (slug, options) => {
    if (!enabled || isSignedIn !== true) {
      openUserProfileTab(settingsSectionLabel(slug))
      return
    }
    const href = options?.hash
      ? `${settingsSectionHref(slug)}#${options.hash}`
      : settingsSectionHref(slug)
    if (options?.newTab) {
      window.open(href, '_blank', 'noopener,noreferrer')
    } else {
      navigate(href)
    }
  }
}
